import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { clockTime } from "../lib/format";
import type { RunEvent } from "../types";
import { ScrollArea } from "./ui/scroll-area";
import { Skeleton } from "./ui/skeleton";

/** A run's event stream as a compact, monospace log. */
export function RunTimeline({
  runId,
  live,
  className,
}: {
  runId: string;
  live?: boolean;
  className?: string;
}) {
  const events = useQuery({
    queryKey: ["run-events", runId],
    queryFn: () => api.runEvents(runId),
  });
  const streamed = useQuery({
    queryKey: ["run-live", runId],
    queryFn: () => Promise.resolve([] as RunEvent[]),
    enabled: false,
    initialData: [] as RunEvent[],
  });
  const list = [...(events.data?.events ?? []), ...streamed.data].sort(
    (a, b) => a.seq - b.seq || a.createdAt.localeCompare(b.createdAt),
  );
  return (
    <ScrollArea className={cn("h-72 rounded-lg border bg-muted/40", className)}>
      <ol className="space-y-1.5 p-3 font-mono text-xs leading-relaxed">
        {events.isLoading &&
          [0, 1, 2].map((i) => (
            <li key={i}>
              <Skeleton className="h-4 w-3/4" />
            </li>
          ))}
        {!events.isLoading && list.length === 0 && (
          <li className="text-muted-foreground">{live ? "Waiting for the agent…" : "No events yet."}</li>
        )}
        {list.map((event) => (
          <li key={event.id} className="flex gap-3">
            <span className="w-10 shrink-0 text-muted-foreground tabular-nums">
              {clockTime(event.createdAt)}
            </span>
            <EventLine type={event.type} payload={event.payload} />
          </li>
        ))}
      </ol>
    </ScrollArea>
  );
}

export function EventLine({ type, payload }: { type: string; payload: Record<string, unknown> }) {
  const base = "min-w-0 break-words";
  if (type === "assistant" || type === "user") {
    return (
      <span
        className={cn(base, type === "user" ? "text-violet-700 dark:text-violet-300" : "text-foreground")}
      >
        {type === "user" && <span className="font-semibold">you › </span>}
        {String(payload.text ?? "")}
      </span>
    );
  }
  if (type === "thinking") return <span className="text-muted-foreground italic">thinking…</span>;
  if (type === "tool_call") {
    return (
      <span className={cn(base, "text-sky-700 dark:text-sky-300")}>
        {String(payload.name ?? "tool")}{" "}
        <span className="text-muted-foreground">{String(payload.status ?? "")}</span>
        {payload.args !== undefined && (
          <span className="ml-1 text-muted-foreground">{truncate(JSON.stringify(payload.args), 100)}</span>
        )}
      </span>
    );
  }
  if (type === "status" || type === "skynet.status") {
    return (
      <span className="text-muted-foreground">status → {String(payload.to ?? payload.status ?? "")}</span>
    );
  }
  if (type === "skynet.mcp") {
    const attached = Array.isArray(payload.attached) ? (payload.attached as string[]) : [];
    const skipped = Array.isArray(payload.skipped)
      ? (payload.skipped as Array<{ id: string; reason: string }>)
      : [];
    return (
      <span className={cn(base, "text-muted-foreground")}>
        tools: {attached.length ? attached.join(", ") : "none"}
        {skipped.length > 0 && <> · skipped {skipped.map((s) => `${s.id} (${s.reason})`).join(", ")}</>}
      </span>
    );
  }
  if (type.startsWith("skynet.")) {
    return (
      <span className={cn(base, "text-muted-foreground")}>
        {type.replace("skynet.", "")} {truncate(summarize(payload), 140)}
      </span>
    );
  }
  if (type === "result")
    return (
      <span className={cn(base, "text-emerald-700 dark:text-emerald-300")}>
        ✓ {truncate(String(payload.text ?? payload.status ?? ""), 160)}
      </span>
    );
  if (type === "error")
    return (
      <span className={cn(base, "text-destructive")}>✕ {String(payload.message ?? payload.code ?? "")}</span>
    );
  return (
    <span className={cn(base, "text-muted-foreground")}>
      {type} {truncate(summarize(payload), 100)}
    </span>
  );
}

function summarize(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload).filter(([k]) => !["payload", "raw"].includes(k));
  return entries.map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`).join(" ");
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
